// =====================================================================
// Worlds catalog — Phase 6+ world variety system.
//
// Each World is a single source of truth for visuals + gameplay flavor:
//   - id / theme : machine identifier (drives painter + dispatch)
//   - name / subtitle : UI strings
//   - mapSize / goal / time : base difficulty (waves multiply these)
//   - zombies : base zombie mix
//   - boss : whether this world spawns a boss
//   - colors : { sky, fog, ground, accent }
//   - fog : { near, far }                  cheap distance-fade
//   - ambient : { intensity, color }       hemispheric light tint
//   - props : [{ maker, count, minDist }]  obstacle placement plan
//   - pacing : { spawnIntervalMult, speedMult } gameplay flavor
//
// To add a new world later: append an entry below and (if it uses a NEW prop
// type) add the component to MapScene.jsx's renderer dispatch. No other code
// needs to know about specific worlds.
// =====================================================================

export const WORLDS = [
  // ----- Existing Phase 1-2 worlds (preserved exactly) -----
  {
    id: 1, theme: "backyard",
    name: "Backyard", subtitle: "Easy starter — basic zombies",
    mapSize: 50, goal: 10, time: 60, boss: false,
    zombies: { regular: 12, fast: 0, big: 0, glowing: 0 },
    colors: { sky: "#1a0d2e", fog: "#2a1547", ground: "#1f3d1f", accent: "#3a6b3a" },
    fog: { near: 30, far: 90 },
    ambient: { intensity: 0.85, color: "#b8a8e6" },
    props: [
      { maker: "tree", count: 10, minDist: 4 },
      { maker: "fence", count: 6, minDist: 5 },
      { maker: "shed", count: 3, minDist: 6 },
    ],
    pacing: { spawnIntervalMult: 1.0, speedMult: 1.0 },
  },
  {
    id: 2, theme: "street",
    name: "Neighborhood Street", subtitle: "Easy-medium",
    mapSize: 70, goal: 18, time: 75, boss: false,
    zombies: { regular: 16, fast: 6, big: 0, glowing: 0 },
    colors: { sky: "#170a2b", fog: "#2b1450", ground: "#2b2f3a", accent: "#4a5060" },
    fog: { near: 32, far: 95 },
    ambient: { intensity: 0.85, color: "#b8a8e6" },
    props: [
      { maker: "house", count: 8, minDist: 7 },
      { maker: "car", count: 6, minDist: 4 },
      { maker: "mailbox", count: 8, minDist: 3 },
      { maker: "tree", count: 8, minDist: 4 },
    ],
    pacing: { spawnIntervalMult: 1.0, speedMult: 1.0 },
  },
  {
    id: 3, theme: "farm",
    name: "Cornfield Farm", subtitle: "Medium — crops and barns",
    mapSize: 90, goal: 25, time: 90, boss: false,
    zombies: { regular: 22, fast: 8, big: 0, glowing: 2 },
    colors: { sky: "#1d0d33", fog: "#321858", ground: "#3a2a18", accent: "#8a6028" },
    fog: { near: 28, far: 85 },
    ambient: { intensity: 0.78, color: "#d9c089" },
    props: [
      { maker: "barn", count: 3, minDist: 10 },
      { maker: "haybale", count: 16, minDist: 3 },
      { maker: "fence", count: 10, minDist: 5 },
      { maker: "tree", count: 6, minDist: 5 },
      { maker: "cornstalk", count: 28, minDist: 2 },
    ],
    pacing: { spawnIntervalMult: 0.9, speedMult: 1.0 },
  },
  {
    id: 4, theme: "junkyard",
    name: "Junkyard", subtitle: "Medium-hard — tighter paths",
    mapSize: 90, goal: 30, time: 100, boss: false,
    zombies: { regular: 22, fast: 10, big: 6, glowing: 2 },
    colors: { sky: "#160828", fog: "#2a1145", ground: "#2a2622", accent: "#9a7028" },
    fog: { near: 28, far: 80 },
    ambient: { intensity: 0.78, color: "#c8b890" },
    props: [
      { maker: "car", count: 10, minDist: 5 },
      { maker: "junk", count: 14, minDist: 3 },
      { maker: "tire", count: 12, minDist: 2 },
      { maker: "fence", count: 4, minDist: 5 },
    ],
    pacing: { spawnIntervalMult: 1.0, speedMult: 1.0 },
  },
  {
    id: 5, theme: "downtown",
    name: "Downtown", subtitle: "Hard — boss level",
    mapSize: 110, goal: 40, time: 120, boss: true,
    zombies: { regular: 28, fast: 12, big: 6, glowing: 4 },
    colors: { sky: "#120625", fog: "#250e4a", ground: "#1f2230", accent: "#3a3f55" },
    fog: { near: 30, far: 90 },
    ambient: { intensity: 0.82, color: "#b8a8e6" },
    props: [
      { maker: "building", count: 14, minDist: 7 },
      { maker: "streetlight", count: 12, minDist: 5 },
      { maker: "car", count: 8, minDist: 4 },
    ],
    pacing: { spawnIntervalMult: 1.0, speedMult: 1.05 },
  },

  // ----- NEW Phase 6 worlds (Feb 2026) -----
  {
    id: 6, theme: "carnival",
    name: "Haunted Carnival", subtitle: "Chaotic — fast waves",
    mapSize: 95, goal: 32, time: 95, boss: false,
    zombies: { regular: 22, fast: 16, big: 2, glowing: 6 },
    colors: { sky: "#2b0e3a", fog: "#5b1a6e", ground: "#3a1c4e", accent: "#ff5fb4" },
    fog: { near: 24, far: 70 },
    ambient: { intensity: 0.95, color: "#ff8fd0" },
    props: [
      { maker: "tent", count: 4, minDist: 9 },
      { maker: "booth", count: 6, minDist: 5 },
      { maker: "streetlight", count: 8, minDist: 6 },
      { maker: "fence", count: 5, minDist: 5 },
    ],
    pacing: { spawnIntervalMult: 0.8, speedMult: 1.1 },
  },
  {
    id: 7, theme: "moonbase",
    name: "Moon Base", subtitle: "Low-grav — elite zombies",
    mapSize: 105, goal: 36, time: 105, boss: false,
    zombies: { regular: 16, fast: 10, big: 10, glowing: 10 },
    colors: { sky: "#03040d", fog: "#0a0f1a", ground: "#5a5a62", accent: "#84e6ff" },
    fog: { near: 50, far: 140 },
    ambient: { intensity: 0.55, color: "#c8d4ff" },
    props: [
      { maker: "moonrock", count: 14, minDist: 4 },
      { maker: "crater", count: 6, minDist: 8 },
      { maker: "alientube", count: 4, minDist: 7 },
      { maker: "fence", count: 4, minDist: 6 },
    ],
    pacing: { spawnIntervalMult: 1.0, speedMult: 0.92 },
  },
  {
    id: 8, theme: "swamp",
    name: "Toxic Swamp", subtitle: "Crawlers — slow & sticky",
    mapSize: 90, goal: 30, time: 100, boss: false,
    zombies: { regular: 30, fast: 4, big: 6, glowing: 6 },
    colors: { sky: "#0e1a0a", fog: "#1c3a1a", ground: "#243818", accent: "#6fff7a" },
    fog: { near: 18, far: 55 },
    ambient: { intensity: 0.7, color: "#7eff90" },
    props: [
      { maker: "tree", count: 12, minDist: 4 },
      { maker: "toxicpuddle", count: 10, minDist: 4 },
      { maker: "junk", count: 6, minDist: 4 },
      { maker: "fence", count: 4, minDist: 5 },
    ],
    pacing: { spawnIntervalMult: 1.1, speedMult: 0.85 },
  },
  {
    id: 9, theme: "neoncity",
    name: "Neon City", subtitle: "Cyberpunk rush",
    mapSize: 105, goal: 38, time: 100, boss: false,
    zombies: { regular: 22, fast: 22, big: 4, glowing: 10 },
    colors: { sky: "#06051c", fog: "#1a0a2e", ground: "#13123a", accent: "#ff2bdc" },
    fog: { near: 26, far: 80 },
    ambient: { intensity: 1.0, color: "#ff5fff" },
    props: [
      { maker: "building", count: 12, minDist: 7 },
      { maker: "neonsign", count: 10, minDist: 5 },
      { maker: "streetlight", count: 10, minDist: 5 },
      { maker: "car", count: 6, minDist: 5 },
    ],
    pacing: { spawnIntervalMult: 0.75, speedMult: 1.15 },
  },
  {
    id: 10, theme: "graveyard",
    name: "Graveyard", subtitle: "Foggy — undead clusters",
    mapSize: 90, goal: 32, time: 100, boss: false,
    zombies: { regular: 30, fast: 6, big: 4, glowing: 8 },
    colors: { sky: "#0a0a16", fog: "#1a1830", ground: "#1c1e22", accent: "#8b86b8" },
    fog: { near: 16, far: 50 },
    ambient: { intensity: 0.55, color: "#9a9ad0" },
    props: [
      { maker: "tombstone", count: 22, minDist: 2.5 },
      { maker: "tree", count: 8, minDist: 5 },
      { maker: "fence", count: 6, minDist: 5 },
      { maker: "junk", count: 4, minDist: 4 },
    ],
    pacing: { spawnIntervalMult: 0.95, speedMult: 0.95 },
  },
  {
    id: 11, theme: "alienlab",
    name: "Alien Lab", subtitle: "Final frontier — boss zone",
    mapSize: 110, goal: 42, time: 115, boss: true,
    zombies: { regular: 18, fast: 14, big: 10, glowing: 14 },
    colors: { sky: "#000810", fog: "#06122a", ground: "#1c2842", accent: "#5fffcf" },
    fog: { near: 28, far: 95 },
    ambient: { intensity: 0.9, color: "#7ee8ff" },
    props: [
      { maker: "alientube", count: 10, minDist: 5 },
      { maker: "building", count: 8, minDist: 7 },
      { maker: "neonsign", count: 6, minDist: 5 },
      { maker: "junk", count: 6, minDist: 4 },
    ],
    pacing: { spawnIntervalMult: 0.8, speedMult: 1.1 },
  },
];

export function getWorld(id) {
  return WORLDS.find((w) => w.id === id) || WORLDS[0];
}

export const WORLD_COUNT = WORLDS.length;
